import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import { i18n } from '#features/i18n/i18n';
import { ApiError } from './ApiError';

/**
 * Сетевой отказ показывается одним тостом, сколько бы действий ни упало разом: текст у них один
 * и тот же - "нет соединения" или "ошибка сети".
 */
export const NETWORK_TOAST_ID = 'network-error';

/**
 * Статусы, на которых сообщение из `gqlFetcher` уже написано для человека и переведено:
 * нет сети (0) и обслуживание/сбой апстрима (502/503/504). Остальное приходит текстом от шлюза —
 * «Variable 'input' has an invalid value…» и подобным, — и показывать его нельзя.
 */
const HUMAN_READABLE_STATUSES = new Set([0, 502, 503, 504]);

/** Запрос не дошёл до сервера: `gqlFetcher` отдаёт статус 0, голый `fetch` - `TypeError`. */
export const isNetworkFailure = (error: unknown) =>
  (error instanceof ApiError && error.status === 0) ||
  (error instanceof Error && error.name === 'TypeError');

/** Отмена запроса (уход со страницы, новый запрос вместо старого) - не отказ, говорить не о чем. */
export const isAbortedRequest = (error: unknown) =>
  error instanceof Error && error.name === 'AbortError';

/**
 * Перевод, который не бросает без локали.
 *
 * Текст ошибки просят в самый неудобный момент: из `onError` мутации и из `catch` кнопки. Если
 * каталог ещё не активен (ранний сбой при старте, рендер на сервере), Lingui бросает «translation
 * function without setting a locale», и вместо тоста человек не получает ничего - исключение из
 * `onError` никто не видит. Английская фраза из исходника лучше, чем тишина.
 */
const translate = (descriptor: MessageDescriptor, english: string): string =>
  i18n.locale ? i18n._(descriptor) : english;

/**
 * Единое правило выбора текста для любого сбоя действия.
 *
 * - сеть: сообщение `gqlFetcher` («Нет соединения…» / «Не удалось связаться…»), а для голого
 *   `TypeError` от `fetch` - то же «Не удалось связаться…»;
 * - обслуживание и сбой апстрима (502/503/504): сообщение `gqlFetcher`, оно написано для человека;
 * - всё остальное - «Что-то пошло не так». Сырой текст шлюза показывать нельзя.
 *
 * Отказы сервера с понятным текстом приходят не исключением, а полем ответа (`GlobalError`) и
 * разбираются в месте вызова - они до сюда не доходят.
 */
export const resolveErrorText = (error: unknown): string => {
  if (error instanceof ApiError && HUMAN_READABLE_STATUSES.has(error.status)) {
    return error.message;
  }

  if (isNetworkFailure(error)) {
    return translate(
      msg({
        id: 'common:network.error',
        message: 'Network error. Please check your connection and try again.',
      }),
      // Сборка вырезает `message` из дескриптора, поэтому запасная фраза нужна отдельной строкой.
      'Network error. Please check your connection and try again.'
    );
  }

  return translate(
    msg({
      id: 'common:error.generic',
      message: 'Something went wrong. Please try again.',
    }),
    'Something went wrong. Please try again.'
  );
};

/**
 * Текст для `onError` мутации, которая показывает отказ сама.
 *
 * Раньше такие обработчики печатали `error.message` как есть. У `ApiError` это либо фраза,
 * написанная `gqlFetcher` для человека (сеть, обслуживание), либо сырой текст шлюза - «Variable
 * 'input' has an invalid value…», - и его читали как ответ сайта. Теперь `ApiError` проходит то же
 * правило, что и общий тост. Обычная ошибка несёт текст того, кто её бросил (`throw new Error(…)`
 * в `mutationFn`), и он остаётся.
 */
export const describeActionError = (error: unknown): string =>
  error instanceof Error && !(error instanceof ApiError) && error.message
    ? error.message
    : resolveErrorText(error);
